import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type {
  DojoDimensionContribution,
  DojoPracticeActivity,
  DojoPracticeProgress,
  GCLDimension,
} from '@/types/dojo';
import { cn } from '@/lib/utils';
import { PracticeFollowThrough } from './PracticeFollowThrough';
import {
  HOW_DIMENSIONS as DIMENSIONS,
  HOW_DIMENSION_COLOR as DIMENSION_COLOR,
  HOW_DIMENSION_TITLE as DIMENSION_LABEL,
} from '@/lib/howDimensions';

function startOfWeek(value: Date): Date {
  const date = new Date(value);
  const day = date.getDay();
  const distanceFromMonday = day === 0 ? 6 : day - 1;
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - distanceFromMonday);
  return date;
}

function validDate(value?: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function activityFromContributions(
  dimensionProgress: DojoPracticeProgress['dimension_progress']
): DojoPracticeActivity[] {
  const activity = new Map<string, DojoPracticeActivity>();

  DIMENSIONS.forEach((dimension) => {
    const contributions: DojoDimensionContribution[] =
      dimensionProgress?.[dimension]?.contributing_rounds ?? [];

    contributions.forEach((contribution, index) => {
      if (!validDate(contribution.created_at)) return;
      const key = contribution.round_id
        ?? `${contribution.scenario_id ?? 'practice'}:${contribution.created_at}:${index}`;
      const existing = activity.get(key);
      if (existing) {
        if (!existing.dimensions.includes(dimension)) existing.dimensions.push(dimension);
        return;
      }
      activity.set(key, {
        round_id: key,
        scenario_id: contribution.scenario_id ?? null,
        title: contribution.scenario_title?.trim() || 'Practice session',
        completed_at: contribution.created_at ?? '',
        dimensions: [dimension],
      });
    });
  });

  return [...activity.values()].sort(
    (a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
  );
}

function activityMarkerStyle(dimensions: GCLDimension[]): CSSProperties {
  const colors = dimensions.map((dimension) => DIMENSION_COLOR[dimension]).filter(Boolean);
  if (colors.length <= 1) {
    return { backgroundColor: colors[0] ?? 'var(--how-accent-warm)' };
  }
  const slice = 100 / colors.length;
  const stops = colors.flatMap((color, index) => [
    `${color} ${index * slice}%`,
    `${color} ${(index + 1) * slice}%`,
  ]);
  return { background: `conic-gradient(${stops.join(', ')})` };
}

interface PracticeRhythmProps {
  practiceProgress: DojoPracticeProgress | null;
  userId?: string;
  preview?: boolean;
}

export function PracticeRhythm({ practiceProgress, userId, preview = false }: PracticeRhythmProps) {
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const activity = useMemo(() => {
    const supplied = practiceProgress?.activity?.filter((item) => validDate(item.completed_at)) ?? [];
    return supplied.length > 0
      ? [...supplied].sort(
          (a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
        )
      : activityFromContributions(practiceProgress?.dimension_progress);
  }, [practiceProgress]);
  const developmentSignal = useMemo(() => {
    const candidates = DIMENSIONS.flatMap((dimension) => {
      const progress = practiceProgress?.dimension_progress?.[dimension];
      const copy = progress?.working_read?.replace(/\s+/g, ' ').trim();
      if (!progress?.has_signal || !copy || /(?:still quiet|not enough)/i.test(copy)) return [];
      return [{ dimension, copy, confidence: progress.confidence ?? 0 }];
    });

    return candidates.sort((a, b) => b.confidence - a.confidence)[0] ?? null;
  }, [practiceProgress?.dimension_progress]);

  if (!practiceProgress || practiceProgress.rounds_completed <= 0) return null;

  const today = new Date();
  const currentWeek = startOfWeek(today);
  const nextWeek = new Date(currentWeek);
  nextWeek.setDate(nextWeek.getDate() + 7);
  const thisWeekActivity = activity.filter((item) => {
    const date = validDate(item.completed_at);
    return date && date >= currentWeek && date < nextWeek;
  });
  const thisWeekDimensions = new Set(thisWeekActivity.flatMap((item) => item.dimensions));
  const month = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const leadingDays = (month.getDay() + 6) % 7;
  const days = Array.from({ length: daysInMonth }, (_, index) => {
    const date = new Date(month.getFullYear(), month.getMonth(), index + 1);
    const entries = activity.filter((item) => {
      const completed = validDate(item.completed_at);
      return completed && completed.getFullYear() === date.getFullYear()
        && completed.getMonth() === date.getMonth() && completed.getDate() === date.getDate();
    });
    return { date, entries, dimensions: DIMENSIONS.filter((dimension) =>
      entries.some((item) => item.dimensions.includes(dimension))) };
  });
  const selected = selectedDay === null ? null : days[selectedDay - 1];
  const changeMonth = (offset: number) => {
    setMonthOffset(offset);
    setSelectedDay(null);
  };

  return (
    <div className="mx-auto w-full max-w-[680px]">
      {developmentSignal && (
        <section
          aria-labelledby="development-signal-title"
          className="border-b border-how-space-deep/[0.08] py-5"
        >
          <p className="text-[10px] font-medium uppercase leading-4 tracking-[0.08em] text-how-text-secondary/50">
            What may be changing
          </p>
          <h2
            id="development-signal-title"
            className="mt-1 font-body text-base font-medium leading-5 tracking-[-0.015em] text-how-space-deep"
          >
            {DIMENSION_LABEL[developmentSignal.dimension]}
          </h2>
          <p className="mt-1.5 max-w-xl text-[13px] leading-5 text-how-text-secondary/65">
            {developmentSignal.copy}
          </p>
        </section>
      )}

      <section
        aria-labelledby="practice-rhythm-title"
        className="mt-8 rounded-2xl border border-how-space-deep/[0.06] bg-white/70 px-5 py-5 shadow-surface sm:px-6"
      >
        <header>
          <h2
            id="practice-rhythm-title"
            className="font-body text-base font-medium leading-5 tracking-[-0.015em] text-how-space-deep"
          >
            Practice rhythm
          </h2>
          <p className="mt-1 text-[13px] leading-5 text-how-text-secondary/85">
            A view of your recent practice.
          </p>
        </header>

        {(
          <div className="mt-5 grid grid-cols-3 border-y border-how-space-deep/[0.06] py-4">
            <div>
              <p className="text-[11px] leading-4 text-how-text-secondary/85">This week</p>
              <p className="mt-0.5 font-body text-lg font-medium leading-6 text-how-space-deep">
                {thisWeekActivity.length}
              </p>
              <p className="text-[11px] leading-4 text-how-text-secondary/85">
                {thisWeekActivity.length === 1 ? 'practice' : 'practices'}
              </p>
            </div>
            <div className="border-l border-how-space-deep/[0.06] pl-4 sm:pl-6">
              <p className="text-[11px] leading-4 text-how-text-secondary/85">Dimensions practiced</p>
              <p className="mt-0.5 font-body text-lg font-medium leading-6 text-how-space-deep">
                {thisWeekDimensions.size}
              </p>
              <p className="text-[11px] leading-4 text-how-text-secondary/85">this week</p>
            </div>
            <div className="border-l border-how-space-deep/[0.06] pl-4 sm:pl-6">
              <p className="text-[11px] leading-4 text-how-text-secondary/85">All time</p>
              <p className="mt-0.5 font-body text-lg font-medium leading-6 text-how-space-deep">
                {practiceProgress.rounds_completed}
              </p>
              <p className="text-[11px] leading-4 text-how-text-secondary/85">
                {practiceProgress.rounds_completed === 1 ? 'session' : 'sessions'}
              </p>
            </div>
          </div>
        )}

        <div className="mt-5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-body text-sm font-medium text-how-space-deep" aria-live="polite">
              {monthLabel}
            </h3>
            <div className="flex gap-1">
              <button type="button" aria-label="Previous month"
                onClick={() => changeMonth(monthOffset - 1)}
                className="min-h-11 rounded-lg px-3 text-xs text-how-text-secondary hover:bg-how-space-deep/5 focus-visible:outline focus-visible:outline-2">
                Previous
              </button>
              <button type="button" aria-label="Next month" disabled={monthOffset >= 0}
                onClick={() => changeMonth(monthOffset + 1)}
                className="min-h-11 rounded-lg px-3 text-xs text-how-text-secondary hover:bg-how-space-deep/5 disabled:opacity-40 focus-visible:outline focus-visible:outline-2">
                Next
              </button>
            </div>
          </div>
          <p className="mt-1 text-xs leading-5 text-how-text-secondary/85">
            Select an active day to see what you practiced.
          </p>
          <div className="mt-4 grid grid-cols-7 gap-1" aria-label={`Practice activity for ${monthLabel}`}>
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <span key={day} className="pb-2 text-center text-[11px] text-how-text-secondary/85">{day}</span>
            ))}
            {Array.from({ length: leadingDays }, (_, index) => <span key={`empty-${index}`} />)}
            {days.map(({ date, entries, dimensions }) => {
              const day = date.getDate();
              const isToday = date.toDateString() === today.toDateString();
              const label = `${date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}: ${entries.length} ${entries.length === 1 ? 'practice' : 'practices'}${dimensions.length ? ', ' + dimensions.map((dimension) => DIMENSION_LABEL[dimension]).join(', ') : ''}`;
              const content = <>
                <span>{day}</span>
                <span className="flex h-2 items-center justify-center gap-0.5" aria-hidden="true">
                  {dimensions.map((dimension) => <span key={dimension} className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: DIMENSION_COLOR[dimension] }} />)}
                </span>
              </>;
              const className = cn(
                'flex min-h-[52px] min-w-0 flex-col items-center justify-center gap-1 rounded-lg text-xs',
                isToday && 'ring-1 ring-inset ring-how-space-deep/20',
                entries.length ? 'bg-how-space-deep/[0.04] text-how-space-deep' : 'text-how-text-secondary/85',
                selectedDay === day && 'ring-2 ring-inset ring-how-space-deep/60'
              );
              return entries.length ? (
                <button key={day} type="button" aria-label={label} aria-pressed={selectedDay === day}
                  aria-current={isToday ? 'date' : undefined}
                  onClick={() => setSelectedDay(selectedDay === day ? null : day)}
                  className={cn(className, 'hover:bg-how-space-deep/[0.08] focus-visible:outline focus-visible:outline-2')}>
                  {content}
                </button>
              ) : <div key={day} aria-label={label} aria-current={isToday ? 'date' : undefined} className={className}>{content}</div>;
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-how-space-deep/[0.06] pt-4">
            {DIMENSIONS.map((dimension) => (
              <span key={dimension} className="flex items-center gap-1.5 text-[11px] text-how-text-secondary/85">
                <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ backgroundColor: DIMENSION_COLOR[dimension] }} />
                {DIMENSION_LABEL[dimension]}
              </span>
            ))}
          </div>
          <div aria-live="polite">
            {selected && (
              <div className="mt-4 border-t border-how-space-deep/[0.06] pt-4">
                <p className="text-xs font-medium text-how-space-deep">
                  {selected.date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
                </p>
                <ul className="mt-2 space-y-3">
                  {selected.entries.map((item) => (
                    <li key={item.round_id} className="flex items-start gap-2">
                      <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full" style={activityMarkerStyle(item.dimensions)} />
                      <div>
                        <p className="text-sm text-how-space-deep">{item.title}</p>
                        <p className="text-xs leading-5 text-how-text-secondary/85">
                          {item.dimensions.map((dimension) => DIMENSION_LABEL[dimension]).join(' · ')}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!days.some((day) => day.entries.length) && (
              <p className="mt-4 text-xs leading-5 text-how-text-secondary/85">No completed practices this month.</p>
            )}
          </div>
        </div>
      </section>

      <PracticeFollowThrough key={String(preview) + (userId ?? '')} activity={activity} userId={userId} preview={preview} />
    </div>
  );
}

